import * as React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { ANIMATIONS } from '../../utils';
import { Heading, Text } from '../design-system';
import { TronDivider } from '../global/TronDivider';

import { RecommendationCard } from './RecommendationCard';

const Wrapper = styled(motion.div)`
  padding: 0 40px 0;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin-right: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 0;
  }
`;

const HeadingWrapper = styled.div`
  text-align: center;
`;

const GridWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE_WIDE};
  justify-content: center;
`;

const StyledText = styled(Text).attrs({ size: 'large' })`
  max-width: 500px;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    font-size: ${({ theme }) => theme.fontSize.MEDIUM};
    line-height: ${({ theme }) => theme.lineHeight.MEDIUM};
    margin-bottom: 10px;
  }
`;

export interface RecommendedBlogPost {
  __typename: string;
  author: string;
  date: string;
  heroImage?: {
    title: string;
    url: string;
  };
  seoMetaDescription: string;
  slug: string;
  title: string;
}

export interface RecommendedUseCase {
  __typename: string;
  author?: string;
  category: {
    slug: string;
  };
  gridImage: {
    title: string;
    url: string;
  };
  name: string;
  seoMetaDescription: string;
  slug: string;
}

export interface RecommendedUseCaseCategory {
  __typename: string;
  gridImage?: {
    title: string;
    url: string;
  };
  seoMetaDescription: string;
  slug: string;
  socialSharePreviewImage?: {
    title: string;
    url: string;
  };
  title: string;
}

export type RecommendationTypes =
  | RecommendedBlogPost
  | RecommendedUseCase
  | RecommendedUseCaseCategory;

interface RecommendationsProps {
  category?: string[];
  isblog?: boolean;
  items: RecommendationTypes[];
  showTron?: boolean;
}

export const Recommendations: React.ComponentType<RecommendationsProps> =
  React.memo(({ showTron, items, category, isblog = false }) => {
    return (
      <>
        <Wrapper {...ANIMATIONS.SimpleFadeIn}>
          <HeadingWrapper>
            <Heading renderAs="h2" style={{ marginBottom: isblog ? 50 : 0 }}>
              {isblog
                ? `More on ${category ? category[0] : ''}`
                : `See what else Hex can do`}
            </Heading>
          </HeadingWrapper>
          {!isblog && (
            <StyledText size="large">
              Discover how other data scientists and analysts use Hex for
              everything from dashboards to deep dives.
            </StyledText>
          )}
          <GridWrapper>
            {items.map((item, index: number) => (
              <RecommendationCard key={index} item={item} />
            ))}
          </GridWrapper>
        </Wrapper>
        {showTron && <TronDivider />}
      </>
    );
  });
